import useSWR, { mutate } from 'swr';
import { Dataset, DatasetVisibility, PermissionLevel, ImportStatus } from '@/lib/dataset/types';
import { useUser } from '@/hooks/use-user';
import { useActiveWorkspace } from '@/lib/workspace';
import { datasetsRecentlyViewedDB, RecentlyViewedDataset } from './datasets-recently-viewed-db';
import { useCallback, useEffect } from 'react';

// Add explicit return type interface
interface UseDatasetResult {
  datasets: Dataset[];
  isLoading: boolean;
  error: string | null;
  deleteDataset: (datasetId: string) => Promise<{ success: boolean; error: null | Error }>;
  getRecentlyViewedDatasets: (limit?: number) => Promise<Dataset[]>;
  addRecentlyViewed: (item: Omit<RecentlyViewedDataset, 'timestamp'>) => Promise<void>;
  removeRecentlyViewed: (id: string) => Promise<void>;
}

export function useDatasets(): UseDatasetResult {
  const { user } = useUser();
  const { activeWorkspaceId } = useActiveWorkspace();
  
  const fetcher = async (): Promise<Dataset[]> => {
    try {
      if (!user || !activeWorkspaceId) {
        return [];
      }

      // Fetch datasets via workspace API endpoint (no longer using Supabase directly)
      const response = await fetch(`/api/workspaces/${activeWorkspaceId}/datasets?expandTables=false`);
      
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        console.error('Error fetching workspace datasets:', response.status, errorText);
        throw new Error(`Failed to fetch datasets: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (!result.datasets) {
        return [];
      }

      // Result already contains transformed Dataset objects from the API
      return result.datasets;
    } catch (error) {
      console.error('Error in datasets fetcher:', error);
      throw error;
    }
  };

  // Use workspace ID and user ID in the cache key to make it reactive to both changes
  // Use a static key when no user or workspace to ensure consistent behavior
  const cacheKey = user && activeWorkspaceId ? `workspace-datasets-${activeWorkspaceId}-${user.id}` : 'no-workspace-datasets';

  const { data: datasets, error, isLoading } = useSWR<Dataset[]>(
    cacheKey,
    fetcher,
    {
      keepPreviousData: true,
      revalidateOnFocus: true
    }
  );

  // Ensure datasets is always an array
  const safeDatasets: Dataset[] = datasets || [];

  // Sync recently viewed cache with actual datasets
  const syncRecentlyViewedCache = useCallback(async (currentDatasets: Dataset[]) => {
    if (!currentDatasets || currentDatasets.length === 0) return;

    try {
      // Get all recently viewed uploaded datasets from IndexedDB
      const recentlyViewed = await datasetsRecentlyViewedDB.recentlyViewed
        .where('source_type')
        .equals('uploaded')
        .toArray();

      // Check which recently viewed datasets no longer exist
      const existingDatasetIds = new Set(currentDatasets.map(d => d.id));
      const invalidRecentlyViewed = recentlyViewed.filter(
        recent => !existingDatasetIds.has(recent.id)
      );

      // Remove invalid entries from the cache
      if (invalidRecentlyViewed.length > 0) {
        const idsToDelete = invalidRecentlyViewed.map(item => item.id);
        await datasetsRecentlyViewedDB.recentlyViewed.bulkDelete(idsToDelete);
        console.log(`Removed ${invalidRecentlyViewed.length} invalid dataset(s) from recently viewed cache:`, idsToDelete);
      }
    } catch (error) {
      console.error('Error syncing recently viewed cache:', error);
    }
  }, []);

  // Sync the cache whenever datasets change
  useEffect(() => {
    if (safeDatasets.length > 0 && !isLoading) {
      syncRecentlyViewedCache(safeDatasets);
    }
  }, [safeDatasets, isLoading, syncRecentlyViewedCache]);

  const getRecentlyViewedDatasets = useCallback(async (limit: number = 5): Promise<Dataset[]> => {
    // Always return an array, even if empty
    if (safeDatasets.length === 0) {
      return [];
    }

    try {
      // Get recently viewed uploaded datasets from IndexedDB
      const recentlyViewed = await datasetsRecentlyViewedDB.recentlyViewed
        .where('source_type')
        .equals('uploaded')
        .reverse()
        .sortBy('timestamp');

      // Filter to only include datasets that actually exist and map to full dataset objects
      const validRecentDatasets = recentlyViewed
        .map(recent => safeDatasets.find(d => d.id === recent.id))
        .filter((dataset): dataset is Dataset => dataset !== undefined)
        .slice(0, limit);

      return validRecentDatasets;
    } catch (error) {
      console.error('Error fetching recently viewed datasets:', error);
      return [];
    }
  }, [safeDatasets]);

  const addRecentlyViewed = useCallback(async (item: Omit<RecentlyViewedDataset, 'timestamp'>) => {
    // Only allow adding datasets that actually exist for uploaded datasets
    if (item.source_type === 'uploaded' && safeDatasets.length > 0) {
      const datasetExists = safeDatasets.some(d => d.id === item.id);
      if (!datasetExists) {
        console.warn(`Cannot add dataset ${item.id} to recently viewed - dataset does not exist`);
        return;
      }
    }

    try {
      const newItem: RecentlyViewedDataset = {
        ...item,
        timestamp: Date.now()
      };

      // Upsert the item (update if exists, insert if not)
      await datasetsRecentlyViewedDB.recentlyViewed.put(newItem);
      
      // Clean up old items - keep only last 20 items per source type
      const allItems = await datasetsRecentlyViewedDB.recentlyViewed
        .where('source_type')
        .equals(item.source_type)
        .reverse()
        .sortBy('timestamp');
      
      if (allItems.length > 20) {
        const itemsToDelete = allItems.slice(20);
        const idsToDelete = itemsToDelete.map((deletableItem: RecentlyViewedDataset) => deletableItem.id);
        await datasetsRecentlyViewedDB.recentlyViewed.bulkDelete(idsToDelete);
      }
    } catch (error) {
      console.error('Error adding recently viewed item:', error);
    }
  }, [safeDatasets]);

  const removeRecentlyViewed = useCallback(async (id: string) => {
    try {
      await datasetsRecentlyViewedDB.recentlyViewed.delete(id);
    } catch (error) {
      console.error('Error removing recently viewed item:', error);
    }
  }, []);

  const deleteDataset = async (datasetId: string) => {
    try {
      const response = await fetch(`/api/datasets/${datasetId}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        console.error('Delete dataset error:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
        throw new Error(`Failed to delete dataset: ${response.statusText}`);
      }
      
      // Also remove from recently viewed cache in IndexedDB
      try {
        await datasetsRecentlyViewedDB.recentlyViewed.delete(datasetId);
        console.log(`Removed dataset ${datasetId} from recently viewed cache`);
      } catch (indexedDbError) {
        // Don't fail the entire operation if IndexedDB cleanup fails
        console.warn('Failed to remove dataset from recently viewed cache:', indexedDbError);
      }
      
      // Invalidate the datasets cache to trigger a refetch
      await mutate(cacheKey);
      
      return { success: true, error: null };
    } catch (error) {
      console.error('Error deleting dataset:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error : new Error('Failed to delete dataset') 
      };
    }
  };

  return {
    datasets: safeDatasets,
    isLoading,
    error: error instanceof Error ? error.message : null,
    deleteDataset,
    // Recently viewed functionality
    getRecentlyViewedDatasets,
    addRecentlyViewed,
    removeRecentlyViewed
  };
} 